import { useQuery } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { IntercomProps, useIntercom } from 'react-use-intercom';

import { PRACTITIONERS_ROLES } from '~/constants/practitioners';
import {
  GetPractitionerWithIntercomDocument,
  GetPractitionerWithIntercomQuery,
  OrganizationContactPoint,
  PractitionerRole,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { intercomUnreadCountState } from '~/state/layouts/OrganizationAppShell/atoms';
import { getWorkPhone } from '~/utils/contact_points';

const MIN_WIDTH = 768;
const LEAST_PADDING = 20;
const PADDING = 75; // メニューに重ならないようにするためのパディング

export const useOperateIntercom = () => {
  const { unreadCount } = useAtomValue(intercomUnreadCountState);
  const { show: openIntercom, update, boot } = useIntercom();
  const existUnderMenu = window.innerWidth <= MIN_WIDTH;

  const { data } = useQuery<GetPractitionerWithIntercomQuery>(GetPractitionerWithIntercomDocument, {
    nextFetchPolicy: 'cache-only',
  });

  useEffect(() => {
    if (!data) return;

    const me = getMe(data);
    if (!me) return;

    const {
      intercomFamilyName,
      intercomGivenName,
      intercomUserId,
      intercomUserHash,
      organization,
      roles,
    } = me;
    const companyName = organization?.company.name;
    const organizationTel = getWorkPhone(
      organization?.organizationContactPoints as OrganizationContactPoint[],
    );
    const mappedRoles = roles.map((role: PractitionerRole) => PRACTITIONERS_ROLES[role]).join(',');

    const bootProps: IntercomProps = {
      name: `${intercomFamilyName}${intercomGivenName}`,
      userId: intercomUserId,
      userHash: intercomUserHash ?? undefined,
    };
    if (organization) {
      bootProps.company = {
        companyId: organization.id,
        name: organization.name,
      };
    }
    if (mappedRoles || companyName || organizationTel) {
      bootProps.customAttributes = {
        roles: mappedRoles,
        companyName: companyName,
        organizationTel: organizationTel,
      };
    }
    boot(bootProps);
  }, [data, boot]);

  useEffect(() => {
    update({
      alignment: existUnderMenu ? 'right' : 'left',
      horizontalPadding: existUnderMenu ? LEAST_PADDING : PADDING,
      verticalPadding: existUnderMenu ? PADDING : LEAST_PADDING,
    });
  }, [update, existUnderMenu]);

  return {
    openIntercom,
    unreadCount,
  };
};
